import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import {
  getAllWorkflowTasks,
  createWorkflowTask,
  moveWorkflowTask,
  getAllEngagements,
  getAllBrands,
  getAllUsers,
} from '../../store/actions';
import { PRIORITY, TASK_TYPE, WAITING_ON, WORKFLOW_STAGE, formatDate, label, options } from '../../constants/garmentCodes';
import useCreateIntent, { CREATE_INTENT } from '../../customHooks/useCreateIntent';

const PRIORITY_COLORS = {
  High: 'bg-red-100 text-red-600',
  Medium: 'bg-amber-100 text-amber-600',
  Low: 'bg-gray-100 text-gray-500',
};
const COLUMNS = options(WORKFLOW_STAGE);
const EMPTY_FORM = { engagementId: '', title: '', type: '', boardColumn: '1', priority: '2', waitingOn: '', assigneeUserId: '', dueDate: '' };
const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400';

function TaskCard({ task, onClick }) {
  const priority = label(PRIORITY, task.priority, '');
  const waitingOn = label(WAITING_ON, task.waitingOn, '');
  return (
    <button
      draggable={!task.__optimistic}
      onDragStart={(e) => e.dataTransfer.setData('text/plain', String(task.id))}
      onClick={onClick}
      className={`w-full bg-white rounded-lg border text-left p-3.5 hover:shadow-md transition-all mb-3 block ${
        task.overdue ? 'border-red-300 bg-red-50/30 shadow-sm' : 'border-gray-200/80'
      } ${task.__optimistic ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-[12px] font-semibold text-gray-800 leading-snug">{task.title}</p>
        {task.overdue && <span className="flex-shrink-0 text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">OVERDUE</span>}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
        {priority && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${PRIORITY_COLORS[priority]}`}>{priority}</span>}
        <span className="text-[10px] text-gray-500">{task.brandName || ''}</span>
      </div>
      <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
        <span>{task.assignee || 'Unassigned'}</span>
        <span className="flex items-center gap-2 flex-shrink-0 text-gray-400">
          {task.comments > 0 && <span>💬 {task.comments}</span>}
          {task.attachments > 0 && <span>📎 {task.attachments}</span>}
        </span>
      </div>
      {waitingOn && (
        <div className={`mt-2 text-[10px] px-2 py-1 rounded font-medium ${
          waitingOn === 'Brand' ? 'bg-amber-50 text-amber-700 border border-amber-200/60' : 'bg-blue-50 text-blue-700 border border-blue-200/60'
        }`}>
          Waiting on {waitingOn}
        </div>
      )}
      {task.dueDate && <p className="text-[10px] text-gray-400 mt-2 font-mono">Due {formatDate(task.dueDate)}</p>}
    </button>
  );
}

export default function TaskBoard() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const [engagementFilter, setEngagementFilter] = useState(params.get('engagementId') || '');
  const [brandFilter, setBrandFilter] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  useCreateIntent(CREATE_INTENT.TASK, () => setAddOpen(true));
  const [view, setView] = useState('board');
  const scrollRef = useRef(null);
  const [drag, setDrag] = useState({ down: false, startX: 0, scrollLeft: 0 });

  const tasks = useSelector((state) => state.workflowtask.tasks);
  const loading = useSelector((state) => state.workflowtask.loading);
  const engagements = useSelector((state) => state.engagement.engagements);
  const brands = useSelector((state) => state.brand.brands);
  const users = useSelector((state) => state.user.users);

  useEffect(() => {
    dispatch(getAllEngagements());
    dispatch(getAllBrands());
    dispatch(getAllUsers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllWorkflowTasks({ engagementId: engagementFilter, brandId: brandFilter }));
  }, [dispatch, engagementFilter, brandFilter]);

  const tasksByColumn = useMemo(
    () =>
      tasks.reduce((acc, t) => {
        (acc[t.boardColumn] = acc[t.boardColumn] || []).push(t);
        return acc;
      }, {}),
    [tasks],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [view]);

  const handleMouseDown = (e) => {
    if (e.target.closest('button, input, a, select, textarea')) return;
    setDrag({ down: true, startX: e.pageX - (scrollRef.current?.offsetLeft || 0), scrollLeft: scrollRef.current?.scrollLeft || 0 });
  };
  const handleMouseUp = () => setDrag((d) => ({ ...d, down: false }));
  const handleMouseMove = (e) => {
    if (!drag.down || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    scrollRef.current.scrollLeft = drag.scrollLeft - (x - drag.startX) * 1.5;
  };

  const move = (id, boardColumn) => {
    dispatch(moveWorkflowTask({ id, boardColumn: Number(boardColumn) }));
    setSelectedTask((t) => (t && String(t.id) === String(id) ? { ...t, boardColumn: Number(boardColumn) } : t));
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const num = (v) => (v === '' ? null : Number(v));
  const create = (e) => {
    e.preventDefault();
    dispatch(
      createWorkflowTask({
        payload: {
          engagementId: Number(form.engagementId),
          title: form.title.trim(),
          type: num(form.type),
          boardColumn: num(form.boardColumn),
          priority: num(form.priority),
          waitingOn: num(form.waitingOn),
          assigneeUserId: num(form.assigneeUserId),
          dueDate: form.dueDate || null,
          comments: 0,
          attachments: 0,
          overdue: false,
        },
      }),
    );
    setForm(EMPTY_FORM);
    setAddOpen(false);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#f8f9fa]">
      <Header title="Task Board" breadcrumbs={[{ workspace: true }, { label: 'Task Board' }]} />

      <div className="px-6 py-3 border-b border-gray-100 bg-white flex items-center gap-3 flex-wrap">
        <select value={engagementFilter} onChange={(e) => setEngagementFilter(e.target.value)} className="text-[12px] border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
          <option value="">All Engagements</option>
          {engagements.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="text-[12px] border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none">
          <option value="">All Brands</option>
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <div className="flex gap-1 border border-gray-200 rounded-lg overflow-hidden">
          <button onClick={() => setView('board')} className={`px-3 py-1.5 text-[12px] ${view === 'board' ? 'bg-gray-100 text-gray-800 font-medium' : 'text-gray-400 hover:bg-gray-50'}`}>Board</button>
          <button onClick={() => setView('list')} className={`px-3 py-1.5 text-[12px] ${view === 'list' ? 'bg-gray-100 text-gray-800 font-medium' : 'text-gray-400 hover:bg-gray-50'}`}>List</button>
        </div>
        {loading && <span className="text-[11px] text-gray-400">Loading…</span>}
        <button onClick={() => setAddOpen(true)} className="ml-auto px-3 py-1.5 text-[12px] font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">
          + Add Task
        </button>
      </div>

      {view === 'board' ? (
        <div
          ref={scrollRef}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseUp}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          className={`flex-1 overflow-auto kanban-scroll p-4 pb-20 ${drag.down ? 'cursor-grabbing' : ''}`}
        >
          <div className="flex gap-3.5 min-w-max pb-8 pr-8">
            {COLUMNS.map((col) => {
              const colTasks = tasksByColumn[col.value] || [];
              return (
                <div
                  key={col.value}
                  className="w-64 flex-shrink-0 flex flex-col"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    const id = e.dataTransfer.getData('text/plain');
                    if (id) move(id, col.value);
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-2 px-1 min-w-0">
                    <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide truncate" title={col.label}>{col.label}</span>
                    {colTasks.length > 0 && <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">{colTasks.length}</span>}
                  </div>
                  <div className="bg-gray-50/80 rounded-xl p-2.5 min-h-[140px] flex-1 pb-4 border border-gray-100/60">
                    {colTasks.map((task) => <TaskCard key={task.id} task={task} onClick={() => !task.__optimistic && setSelectedTask(task)} />)}
                    {colTasks.length === 0 && <div className="text-center py-8 text-[11px] text-gray-300">Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-6 pb-20">
          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
            <table className="w-full data-table">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left">Task</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Brand</th>
                  <th className="px-4 py-3 text-left">Stage</th>
                  <th className="px-4 py-3 text-left">Assignee</th>
                  <th className="px-4 py-3 text-left">Due</th>
                  <th className="px-4 py-3 text-left">Priority</th>
                  <th className="px-4 py-3 text-left">Waiting On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tasks.map((task) => {
                  const priority = label(PRIORITY, task.priority, '');
                  const waitingOn = label(WAITING_ON, task.waitingOn, '');
                  return (
                    <tr key={task.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => !task.__optimistic && setSelectedTask(task)}>
                      <td className="px-4 py-3">
                        <span className="text-[12px] font-medium text-gray-800">{task.title}</span>
                        {task.overdue && <span className="ml-2 text-[9px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">OVERDUE</span>}
                      </td>
                      <td className="px-4 py-3 text-[11px] text-gray-500">{label(TASK_TYPE, task.type)}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-700">{task.brandName || '—'}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-700">{label(WORKFLOW_STAGE, task.boardColumn)}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-700">{task.assignee || '—'}</td>
                      <td className="px-4 py-3 text-[12px] text-gray-600">{formatDate(task.dueDate)}</td>
                      <td className="px-4 py-3">{priority && <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${PRIORITY_COLORS[priority]}`}>{priority}</span>}</td>
                      <td className="px-4 py-3">{waitingOn && <Badge status={waitingOn === 'Brand' ? 'Awaiting Brand Approval' : 'In Progress'}>{waitingOn}</Badge>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!loading && tasks.length === 0 && <p className="py-10 text-center text-[12px] text-gray-400">No tasks</p>}
          </div>
        </div>
      )}

      {selectedTask && (
        <div className="fixed inset-0 z-40 flex">
          <div className="flex-1 bg-black/30" onClick={() => setSelectedTask(null)} />
          <div className="w-[420px] bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="text-[14px] font-semibold text-gray-900">{selectedTask.title}</h2>
              <button onClick={() => setSelectedTask(null)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-2 flex-wrap">
                {selectedTask.priority && (
                  <span className={`text-[10px] font-medium px-2 py-1 rounded ${PRIORITY_COLORS[label(PRIORITY, selectedTask.priority)]}`}>{label(PRIORITY, selectedTask.priority)} Priority</span>
                )}
                {selectedTask.waitingOn && (
                  <span className={`text-[10px] font-medium px-2 py-1 rounded ${selectedTask.waitingOn === 1 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                    Waiting on {label(WAITING_ON, selectedTask.waitingOn)}
                  </span>
                )}
                {selectedTask.overdue && <span className="text-[10px] font-bold text-red-600 bg-red-100 px-2 py-1 rounded">OVERDUE</span>}
              </div>
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <div><p className="text-gray-400">Brand</p><p className="font-medium">{selectedTask.brandName || '—'}</p></div>
                <div><p className="text-gray-400">Stage</p><p className="font-medium">{label(WORKFLOW_STAGE, selectedTask.boardColumn)}</p></div>
                <div><p className="text-gray-400">Assignee</p><p className="font-medium">{selectedTask.assignee || '—'}</p></div>
                <div><p className="text-gray-400">Due Date</p><p className="font-medium">{formatDate(selectedTask.dueDate)}</p></div>
                <div><p className="text-gray-400">Type</p><p className="font-medium">{label(TASK_TYPE, selectedTask.type)}</p></div>
                <div><p className="text-gray-400">Linked Item</p><p className="font-medium">{selectedTask.linkedItemType ? `${selectedTask.linkedItemType} #${selectedTask.linkedItemId}` : '—'}</p></div>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">Move to Stage</p>
                <select value={selectedTask.boardColumn ?? ''} onChange={(e) => move(selectedTask.id, e.target.value)} className={inputCls}>
                  {COLUMNS.map((col) => <option key={col.value} value={col.value}>{col.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Create Task" size="md">
        <form onSubmit={create} className="space-y-4">
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Title *</label>
            <input required placeholder="Task description" value={form.title} onChange={set('title')} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Engagement *</label>
              <select required value={form.engagementId} onChange={set('engagementId')} className={inputCls}>
                <option value="">Select engagement</option>
                {engagements.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Priority</label>
              <select value={form.priority} onChange={set('priority')} className={inputCls}>
                {options(PRIORITY).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Assignee</label>
              <select value={form.assigneeUserId} onChange={set('assigneeUserId')} className={inputCls}>
                <option value="">Unassigned</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Due Date</label>
              <input type="date" value={form.dueDate} onChange={set('dueDate')} className={inputCls} />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setAddOpen(false)} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55]">Create Task</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
